import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Printer } from "lucide-react";
import { usePrinters, type PrinterWithJobTypes, type JobType, type PaperWidth, type CutType, type PrinterProtocol } from "@/hooks/usePrinters";
import { PrinterList } from "@/components/printers/PrinterList";
import { PrinterFormDialog } from "@/components/printers/PrinterFormDialog";
import { QZTraySetupGuide } from "@/components/printers/QZTraySetupGuide";
import { PrinterConnectionTest } from "@/components/printers/PrinterConnectionTest";
import { PrintQueueMonitor } from "@/components/printers/PrintQueueMonitor";
import { PrintModeSelector } from "@/components/printers/PrintModeSelector";
import { LocalServerConfig } from "@/components/printers/LocalServerConfig";
import { useUserRole } from "@/hooks/useUserRole";

export default function ImpressorasPage() {
  const {
    printers,
    isLoading,
    createPrinter,
    updatePrinter,
    deletePrinter,
    setDefaultPrinter,
  } = usePrinters();
  const { canAccessSettings } = useUserRole();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<PrinterWithJobTypes | null>(null);

  const handleOpenNew = () => {
    setEditingPrinter(null);
    setDialogOpen(true);
  };

  const handleEdit = (printer: PrinterWithJobTypes) => {
    setEditingPrinter(printer);
    setDialogOpen(true);
  };

  const handleSave = async (
    name: string,
    printerName: string,
    isActive: boolean,
    jobTypes: JobType[],
    settings?: {
      paper_width?: PaperWidth;
      chars_per_line?: number;
      auto_cut_enabled?: boolean;
      cut_type?: CutType;
      extra_lines_before_cut?: number;
      protocol?: PrinterProtocol;
    }
  ) => {
    if (editingPrinter) {
      return updatePrinter(editingPrinter.id, name, printerName, isActive, jobTypes, settings);
    } else {
      return createPrinter(name, printerName, jobTypes, settings);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Printer className="h-6 w-6" />
            Impressoras
          </h1>
          <p className="text-muted-foreground">
            Configure as impressoras e seus tipos de impressão
          </p>
        </div>
        <div className="flex items-center gap-2">
          <QZTraySetupGuide />
          <Button onClick={handleOpenNew}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Impressora
          </Button>
        </div>
      </div>

      <PrintModeSelector />

      {/* Config do servidor local por dispositivo — só admin/gerente */}
      {canAccessSettings && <LocalServerConfig />}

      <div className="grid gap-6 lg:grid-cols-2">
        <PrinterConnectionTest />
        <PrintQueueMonitor />
      </div>

      <PrinterList
        printers={printers}
        onEdit={handleEdit}
        onDelete={deletePrinter}
        onSetDefault={setDefaultPrinter}
      />

      <PrinterFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        printer={editingPrinter}
        onSave={handleSave}
      />
    </div>
  );
}
